import type { KyInstance } from 'ky';
import { formatUnits } from 'viem';

import {
    fetchUserTrades as fetchSpeedUserTrades,
    USDC_DECIMALS,
    type UserTrade,
} from '@/lib/api/speed';

import type { RecoveredTradePayload } from '../types';

export type { UserTrade };

interface FetchUserTradesParams {
    /** Accepted for call-site compatibility; the backend pins /speed/trades to 999. */
    networkId?: number;
    status?: string;
    limit?: number;
    page?: number;
    /** Optional abort signal to cancel the request (e.g. on effect cleanup). */
    signal?: AbortSignal;
}

/**
 * Fetch the authenticated user's trades (GET /speed/trades) for history and
 * reconciliation. Requires the authenticated client (JWT bearer).
 */
export async function fetchUserTrades(
    authedApi: KyInstance,
    params: FetchUserTradesParams = {},
): Promise<UserTrade[]> {
    return fetchSpeedUserTrades(authedApi, {
        status: params.status,
        limit: params.limit,
        page: params.page,
        signal: params.signal,
    });
}

function toCreatedAtIso(createdAt: UserTrade['createdAt']): string {
    return typeof createdAt === 'string' ? createdAt : createdAt.toISOString();
}

/**
 * Convert a UserTrade (GET /speed/trades) into the canonical
 * RecoveredTradePayload for hydration and UI restore. Margin is a 6-decimal
 * USDC base-unit string; entry price is a decimal string.
 */
export function userTradeToRecoveredPayload(
    trade: UserTrade,
): RecoveredTradePayload {
    const entry = trade.entryPrice !== null ? Number(trade.entryPrice) : NaN;
    const amount =
        trade.margin !== null
            ? Number(formatUnits(BigInt(trade.margin), USDC_DECIMALS))
            : NaN;
    if (Number.isNaN(entry) || Number.isNaN(amount)) {
        throw new Error(
            `[tradeApi] Invalid numeric fields for trade ${trade.tradeHash}: ` +
                `entryPrice=${trade.entryPrice}, margin=${trade.margin}`,
        );
    }

    // Derive leverage from notional / margin (qty is the asset size). Falls back
    // to undefined so the live position read (which carries authoritative
    // leverage) is the source of truth when this can't be computed.
    const qty = trade.qty !== null ? Number(trade.qty) : NaN;
    const notional = qty * entry;
    const leverage =
        Number.isFinite(qty) && amount > 0 && Number.isFinite(notional)
            ? Math.max(1, Math.round(notional / amount))
            : undefined;

    return {
        tradeHash: trade.tradeHash,
        openTxHash: trade.openTxHash,
        coin: trade.pairBase,
        side: trade.isLong ? 'LONG' : 'SHORT',
        amount,
        leverage,
        entry,
        pairSymbol: `${trade.pairBase.toLowerCase()}-usd`,
        createdAt: toCreatedAtIso(trade.createdAt),
    };
}
